import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Users, BookOpen, Bell, Check, Calendar, FileText, 
  Image, Video, Loader2, ChevronRight, Crown, Eye,
  ChevronDown, Quote, Shield
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import CoachBSpeak from '../components/CoachBSpeak';

const COLORS = {
  royalBlue: '#002366',
  oxblood: '#800000',
  parchment: '#F5F5DC',
  gold: '#D4AF37',
  darkBg: '#1a1a2e'
};

const LChaimCompanion = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [parentContent, setParentContent] = useState(null);
  const [weekIntro, setWeekIntro] = useState(null);
  const [weekNum, setWeekNum] = useState(1);
  const [isPillarStart, setIsPillarStart] = useState(false);
  const [recentEntries, setRecentEntries] = useState([]);
  const [recentUploads, setRecentUploads] = useState([]);
  const [pendingReviews, setPendingReviews] = useState([]);

  useEffect(() => {
    loadCompanionData();
  }, []);

  const loadCompanionData = async () => {
    try {
      const token = localStorage.getItem('academy_token');
      if (!token) {
        navigate('/academy/lchaim/login');
        return;
      }

      // Get user info
      const userRes = await api.get('/academy/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!userRes.data.success) {
        navigate('/academy/lchaim/login');
        return;
      }
      setUser(userRes.data.user);

      // Get companion view (linked student data)
      const companionRes = await api.get('/academy/lchaim/companion', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (companionRes.data.success) {
        setStudentData(companionRes.data);
        setRecentEntries(companionRes.data.recent_entries || []);
        setRecentUploads(companionRes.data.recent_uploads || []);

        // Load parent content for current day
        if (companionRes.data.progress) {
          const chord = companionRes.data.progress.current_chord || 1;
          const day = companionRes.data.progress.current_day || 1;
          const dayInChord = ((day - 1) % 35) + 1;
          
          try {
            const contentRes = await api.get(`/academy/lchaim/content/${chord}/day/${dayInChord}?content_type=parent`);
            if (contentRes.data.success) {
              setParentContent(contentRes.data.day);
              setWeekIntro(contentRes.data.week_intro || null);
              setWeekNum(contentRes.data.week || 1);
              setIsPillarStart(!!contentRes.data.is_pillar_start);
            }
          } catch (e) {
            console.error('Error loading parent content:', e);
          }
        }
      }

      // Get pending reviews
      const reviewsRes = await api.get('/academy/lchaim/reviews/pending', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (reviewsRes.data.success) {
        setPendingReviews(reviewsRes.data.reviews || []);
      }

    } catch (error) {
      console.error('Error loading companion:', error);
      if (error.response?.status === 404) {
        toast.error('No linked student found. Please link a student account first.');
      } else {
        toast.error('Failed to load companion view');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveChord = async (review) => {
    const wordOfHonor = prompt('Enter your "Word of Honor" - a brief note of encouragement for your son:');
    if (!wordOfHonor) return;

    try {
      const token = localStorage.getItem('academy_token');
      await api.post('/academy/lchaim/reviews/approve', {
        student_id: review.student_id,
        chord: review.chord,
        word_of_honor: wordOfHonor
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Seal of Approval granted! Next Chord unlocked.');
      loadCompanionData();
    } catch (error) {
      toast.error('Failed to submit approval');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.darkBg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: COLORS.gold }} />
      </div>
    );
  }

  const progress = studentData?.progress || {};
  const currentDay = progress.current_day || 1;
  const currentChord = progress.current_chord || 1;
  const progressPercent = ((currentDay - 1) / 350) * 100;

  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.darkBg }}>
      {/* Header */}
      <header className="border-b" style={{ borderColor: COLORS.gold, backgroundColor: COLORS.oxblood }}>
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Users className="w-8 h-8" style={{ color: COLORS.gold }} />
              <div>
                <h1 className="text-xl font-serif font-bold" style={{ color: COLORS.parchment }}>
                  Coach's Companion
                </h1>
                <p className="text-sm font-serif" style={{ color: COLORS.gold }}>
                  Guiding {studentData?.student_name || 'Your Son'} through L'Chaim
                </p>
              </div>
            </div>
            <Button 
              variant="ghost"
              className="font-serif"
              style={{ color: COLORS.parchment }}
              onClick={() => navigate('/academy/lchaim/dashboard')}
            >
              My Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Progress Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Student Progress */}
          <Card style={{ backgroundColor: COLORS.royalBlue, border: `2px solid ${COLORS.gold}` }}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="font-serif text-sm" style={{ color: COLORS.parchment }}>His Journey</span>
                <span className="font-serif font-bold" style={{ color: COLORS.gold }}>Day {currentDay}/350</span>
              </div>
              <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(245,245,220,0.2)' }}>
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${progressPercent}%`, backgroundColor: COLORS.gold }}
                />
              </div>
              <p className="mt-2 text-xs font-serif" style={{ color: COLORS.parchment }}>
                Chord {currentChord} • {350 - currentDay} days remaining
              </p>
            </CardContent>
          </Card>

          {/* Pending Reviews */}
          <Card style={{ backgroundColor: pendingReviews.length > 0 ? COLORS.oxblood : COLORS.royalBlue, border: `2px solid ${COLORS.gold}` }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <Bell className="w-6 h-6" style={{ color: COLORS.gold }} />
                <span className="font-serif text-sm" style={{ color: COLORS.parchment }}>Pending Reviews</span>
              </div>
              <h3 className="text-2xl font-serif font-bold" style={{ color: COLORS.parchment }}>
                {pendingReviews.length}
              </h3>
              <p className="text-sm font-serif mt-1" style={{ color: COLORS.gold }}>
                {pendingReviews.length > 0 ? 'Action Required' : 'All caught up!'}
              </p>
            </CardContent>
          </Card>

          {/* Today's Pillar */}
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-2">
                <BookOpen className="w-6 h-6" style={{ color: COLORS.royalBlue }} />
                <span className="font-serif text-sm" style={{ color: COLORS.oxblood }}>Today's Focus</span>
              </div>
              <h3 className="text-2xl font-serif font-bold" style={{ color: COLORS.royalBlue }}>
                {parentContent?.pillar || 'Respect'}
              </h3>
              <p className="text-sm font-serif mt-1" style={{ color: COLORS.oxblood }}>
                {parentContent?.title || `Day ${currentDay}`}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* This Week's Pillar — same teaching your son receives */}
        {weekIntro && (
          <ParentPillarPanel intro={weekIntro} week={weekNum} defaultOpen={isPillarStart} />
        )}

        {/* Pending Reviews Alert */}
        {pendingReviews.length > 0 && (
          <Card className="mb-8" style={{ backgroundColor: COLORS.oxblood, border: `2px solid ${COLORS.gold}` }}>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.parchment }}>
                <Crown className="w-5 h-5" style={{ color: COLORS.gold }} />
                Seal of Approval Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pendingReviews.map((review, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded mb-2" style={{ backgroundColor: 'rgba(245,245,220,0.1)' }}>
                  <div>
                    <p className="font-serif font-bold" style={{ color: COLORS.parchment }}>
                      Chord {review.chord} Complete
                    </p>
                    <p className="text-sm font-serif" style={{ color: COLORS.gold }}>
                      {review.student_name} is ready for your approval
                    </p>
                  </div>
                  <Button 
                    onClick={() => handleApproveChord(review)}
                    className="font-serif font-bold"
                    style={{ backgroundColor: COLORS.gold, color: COLORS.royalBlue }}
                  >
                    Grant Seal
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-8">
          {/* Parent's Daily Guide */}
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.oxblood}` }}>
            <CardHeader style={{ borderBottom: `2px solid ${COLORS.oxblood}` }}>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.oxblood }}>
                <Calendar className="w-5 h-5" />
                Your Daily Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {parentContent ? (
                <>
                  {/* Coach B reads today's full coaching brief aloud */}
                  <div data-testid="lchaim-companion-voice">
                    <CoachBSpeak
                      text={(() => {
                        const parts = [];
                        if (parentContent.title) parts.push(`${parentContent.title}.`);
                        if (parentContent.the_word) parts.push(`Today's coaching point. ... ${parentContent.the_word} ...`);
                        if (parentContent.activity) parts.push(`Discussion activity. ... ${parentContent.activity}`);
                        if (parentContent.assignment && parentContent.assignment !== parentContent.activity) {
                          parts.push(`... Your assignment. ... ${parentContent.assignment}`);
                        }
                        return parts.join(' ').slice(0, 480);
                      })()}
                      label="Hear Coach B read this"
                      variant="full"
                      accent="#c99a3d"
                      testId="lchaim-companion-coach-b"
                    />
                  </div>
                  {parentContent.the_word && (
                    <div>
                      <h4 className="font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
                        Today's Coaching Point
                      </h4>
                      <p className="font-serif p-4 rounded text-sm" style={{ backgroundColor: COLORS.parchment, color: COLORS.royalBlue }}>
                        {parentContent.the_word}
                      </p>
                    </div>
                  )}
                  {parentContent.activity && (
                    <div>
                      <h4 className="font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
                        Discussion Activity
                      </h4>
                      <p className="font-serif p-4 rounded text-sm" style={{ backgroundColor: COLORS.parchment, color: COLORS.royalBlue }}>
                        {parentContent.activity}
                      </p>
                    </div>
                  )}
                  {parentContent.assignment && (
                    <div>
                      <h4 className="font-serif font-bold mb-2" style={{ color: COLORS.royalBlue }}>
                        Your Assignment
                      </h4>
                      <p className="font-serif p-4 rounded text-sm" style={{ backgroundColor: COLORS.parchment, color: COLORS.royalBlue }}>
                        {parentContent.assignment}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <p className="font-serif text-center py-8" style={{ color: COLORS.oxblood }}>
                  Loading today's guide...
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Student Entries */}
          <Card style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardHeader style={{ borderBottom: `2px solid ${COLORS.royalBlue}` }}>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.royalBlue }}>
                <Eye className="w-5 h-5" />
                His Recent Entries
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {recentEntries.length > 0 ? (
                <div className="space-y-4">
                  {recentEntries.map((entry, idx) => (
                    <div key={idx} className="p-4 rounded" style={{ backgroundColor: COLORS.parchment }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-serif font-bold text-sm" style={{ color: COLORS.royalBlue }}>
                          Day {entry.day}
                        </span>
                        <span className="text-xs font-serif" style={{ color: COLORS.oxblood }}>
                          {entry.updated_at ? new Date(entry.updated_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                      <p className="font-serif text-sm line-clamp-3" style={{ color: COLORS.royalBlue }}>
                        {entry.entry || 'No entry yet'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="font-serif text-center py-8" style={{ color: COLORS.oxblood }}>
                  No journal entries yet. Encourage him to write!
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Vault Uploads */}
        {recentUploads.length > 0 && (
          <Card className="mt-8" style={{ backgroundColor: 'white', border: `2px solid ${COLORS.royalBlue}` }}>
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2" style={{ color: COLORS.royalBlue }}>
                <Image className="w-5 h-5" />
                Recent Vault Uploads
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {recentUploads.map((file, idx) => (
                  <div key={idx} className="p-4 rounded text-center" style={{ backgroundColor: COLORS.parchment }}>
                    {file.content_type?.startsWith('image') ? (
                      <Image className="w-8 h-8 mx-auto mb-2" style={{ color: COLORS.royalBlue }} />
                    ) : file.content_type?.startsWith('video') ? (
                      <Video className="w-8 h-8 mx-auto mb-2" style={{ color: COLORS.royalBlue }} />
                    ) : (
                      <FileText className="w-8 h-8 mx-auto mb-2" style={{ color: COLORS.royalBlue }} />
                    )}
                    <p className="text-xs font-serif truncate" style={{ color: COLORS.oxblood }}>
                      Day {file.day}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export const ParentPillarPanel = ({ intro, week, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  useEffect(() => { setOpen(!!defaultOpen); }, [week, defaultOpen]);

  const Label = ({ children }) => (
    <div className="font-serif font-bold text-[11px] tracking-[0.2em] uppercase mb-1.5" style={{ color: COLORS.oxblood }}>{children}</div>
  );
  const Bullets = ({ items }) => (
    <ul className="space-y-1.5">
      {items.map((b, i) => (
        <li key={i} className="flex gap-2 font-serif text-sm leading-relaxed" style={{ color: COLORS.royalBlue }}>
          <span style={{ color: COLORS.gold }}>◆</span><span>{b}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <Card className="mb-8" style={{ backgroundColor: 'white', border: `2px solid ${COLORS.gold}` }} data-testid="companion-pillar-overview">
      <button onClick={() => setOpen((o) => !o)} className="w-full text-left" data-testid="companion-pillar-toggle">
        <div className="p-6" style={{ borderBottom: open ? `2px solid ${COLORS.parchment}` : 'none' }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <Shield className="w-6 h-6 mt-0.5 flex-shrink-0" style={{ color: COLORS.gold }} />
              <div className="min-w-0">
                <div className="font-serif text-[11px] tracking-[0.2em] uppercase font-bold" style={{ color: COLORS.oxblood }}>
                  This Week's Pillar · Week {week} · Follow along with your son
                </div>
                <h3 className="font-serif text-2xl font-bold leading-tight mt-1" style={{ color: COLORS.royalBlue }}>
                  {intro.title || `The Order of ${intro.pillar}`}
                </h3>
              </div>
            </div>
            <ChevronDown className={`w-6 h-6 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: COLORS.gold }} />
          </div>
          {intro.order && (
            <div className="flex gap-2.5 items-start mt-3 pl-3 border-l-4" style={{ borderColor: COLORS.gold }} data-testid="companion-order-quote">
              <Quote className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.gold }} />
              <p className="font-serif italic text-base leading-relaxed" style={{ color: COLORS.oxblood }}>&ldquo;{intro.order}&rdquo;</p>
            </div>
          )}
          {!open && (
            <div className="mt-2 font-serif text-xs" style={{ color: COLORS.royalBlue, opacity: 0.6 }}>Tap to read this week's full teaching</div>
          )}
        </div>
      </button>

      {open && (
        <CardContent className="p-6 pt-5 space-y-5" data-testid="companion-pillar-body">
          {(intro.focus || intro.objective) && (
            <div>
              <Label>The Weekly Order</Label>
              {intro.focus && <p className="font-serif text-sm" style={{ color: COLORS.royalBlue }}><b>Focus:</b> {intro.focus}</p>}
              {intro.objective && <p className="font-serif text-sm mt-1" style={{ color: COLORS.royalBlue }}><b>Objective:</b> {intro.objective}</p>}
            </div>
          )}
          {intro.deep_dive_verse && (
            <div>
              <Label>Deep Dive{intro.deep_dive_ref ? ` · ${intro.deep_dive_ref}` : ''}</Label>
              <p className="font-serif italic text-sm p-3 rounded leading-relaxed" style={{ backgroundColor: COLORS.parchment, color: COLORS.royalBlue }}>&ldquo;{intro.deep_dive_verse}&rdquo;</p>
            </div>
          )}
          {intro.core_logic?.length > 0 && (
            <div><Label>The Core Logic</Label><Bullets items={intro.core_logic} /></div>
          )}
          {intro.key_takeaways?.length > 0 && (
            <div><Label>Key Takeaways</Label><Bullets items={intro.key_takeaways} /></div>
          )}
          {intro.how_to?.length > 0 && (
            <div><Label>How To Coach It This Week</Label><Bullets items={intro.how_to} /></div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default LChaimCompanion;
